import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, Grid, List, ListItemButton, ListItemText, Popover } from '@mui/material';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import { ActionInput, Input, PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import { StainType } from 'interfaces/stainType';
import { Taxonomy } from 'interfaces/taxonomy';
import { Dictionary, filter, findIndex, isEmpty, map, pullAt } from 'lodash';
import React, { useState } from 'react';
import { NewCell } from '..';
import PreviousJobComparisonAlert from '../PreviousJobComparisonAlert';
import type { PreviousJobComparisonStatus } from '../previousJobComparisonStatus';
import { HighlightedPresetInput, PresetActionAccess } from '../Presets/presetUtils';
import { PostProcessingAction } from '../PostProcessingActions/PostProcessingAction';
import { useActionsQuery } from '../PostProcessingActions/usePostProcessingActions';
import { createNewAction } from '../PostProcessingActions/utils';
import { NewCellsModal } from './NewCellsModal';

interface FeatureActionsProps {
  actions: PostProcessingActionCreated[];
  setActions: React.Dispatch<React.SetStateAction<PostProcessingActionCreated[]>>;
  stains: string[];
  getOptions: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => any[];
  getOptionsByValue: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => Dictionary<{ id: string | number; label: string }>;
  getMappingFiltersMetadataForLogicalQuery: (
    stain: string,
    actionInput: string,
    actionAttributesTable: AttributesTable,
    index?: number
  ) => any;
  getAdditionalInputError: (inputValue: any, input: Input, action: PostProcessingActionCreated) => string;
  newCells: Record<string, NewCell>;
  setNewCells: React.Dispatch<React.SetStateAction<Record<string, NewCell>>>;
  previousJobComparisonStatus?: PreviousJobComparisonStatus;
  highlightedInputs?: HighlightedPresetInput[];
  stainTypes?: StainType[];
  taxonomies?: Taxonomy[];
  qcMarkersSettings?: Record<string, string>;
  presetAccessByActionId?: Record<string, PresetActionAccess>;
  onPresetAccessChange?: (actionId: string, access: PresetActionAccess) => void;
  allowNewCellFilters?: boolean;
}

export const FeatureActions: React.FC<React.PropsWithChildren<FeatureActionsProps>> = ({
  actions,
  setActions,
  stains,
  getOptions,
  getOptionsByValue,
  getMappingFiltersMetadataForLogicalQuery,
  getAdditionalInputError,
  newCells,
  setNewCells,
  previousJobComparisonStatus,
  highlightedInputs = [],
  stainTypes = [],
  taxonomies = [],
  qcMarkersSettings = {},
  presetAccessByActionId,
  onPresetAccessChange,
  allowNewCellFilters = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isNewCellsModalOpen, setIsNewCellsModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { postProcessingActionsById, postProcessingActionsFeatures } = useActionsQuery();

  const addNewAction = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, postProcessingActionId: string) => {
    setAnchorEl(null);
    const newAction = createNewAction(postProcessingActionId, postProcessingActionsById);

    setActions((prevActions) => [...prevActions, newAction]);
  };

  const removeAction = (index: number) => {
    setActions((prevActions) => {
      const newActions = [...prevActions];
      pullAt(newActions, index);
      return newActions;
    });
  };

  const setAction = (action: any, index: number) => {
    setActions((prevActions) => {
      let newActions = [...prevActions];
      newActions[index] = action;

      return newActions;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      setActions((prev) => {
        const activeIndex = findIndex(prev, (item) => item.id === active?.id);
        const overIndex = findIndex(prev, (item) => item.id === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  };

  const moveAction = (oldIndex: number, newIndex: number) => {
    if (
      oldIndex === newIndex ||
      oldIndex < 0 ||
      newIndex < 0 ||
      oldIndex >= actions.length ||
      newIndex >= actions.length
    ) {
      return;
    }

    setActions((prevActions) => arrayMove(prevActions, oldIndex, newIndex));
  };

  return (
    <>
      {previousJobComparisonStatus?.hasPreviousJobSettings && (
        <Grid item mb={2}>
          <PreviousJobComparisonAlert
            status={previousJobComparisonStatus}
            modifiedMessage="Features modified from previous job."
            matchMessage="Features match previous job."
          />
        </Grid>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={actions} strategy={verticalListSortingStrategy}>
          <Grid container direction="column">
            {map(actions, (action, index) => {
              return (
                <Grid item mb={1} key={action.id}>
                  <PostProcessingAction
                    key={action.id}
                    id={action.id}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === actions.length - 1}
                    postProcessingActionsById={postProcessingActionsById}
                    action={action}
                    setAction={setAction}
                    removeAction={removeAction}
                    moveActionUp={() => moveAction(index, index - 1)}
                    moveActionDown={() => moveAction(index, index + 1)}
                    getOptions={getOptions}
                    getOptionsByValue={getOptionsByValue}
                    getMappingFiltersMetadataForLogicalQuery={getMappingFiltersMetadataForLogicalQuery}
                    getAdditionalInputError={getAdditionalInputError}
                    presetInputSection="features"
                    highlightedInputs={highlightedInputs}
                    stainTypes={stainTypes}
                    taxonomies={taxonomies}
                    qcMarkersSettings={qcMarkersSettings}
                    presetAuthoring={
                      onPresetAccessChange
                        ? {
                            access: presetAccessByActionId?.[action.id] || 'editable',
                            onAccessChange: (access) => onPresetAccessChange(action.id, access),
                          }
                        : undefined
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
        </SortableContext>
      </DndContext>
      <Button
        aria-describedby="simple-popover"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
      >
        Add New Feature
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          {map(
            filter(postProcessingActionsFeatures, (postProcessingAction) => isEmpty(postProcessingAction.deletedAt)),
            (postProcessingAction) => (
              <ListItemButton
                key={postProcessingAction.id}
                onClick={(event) => addNewAction(event, postProcessingAction.id)}
              >
                <ListItemText primary={postProcessingAction.actionDisplayName ?? postProcessingAction.actionName} />
              </ListItemButton>
            )
          )}
        </List>
      </Popover>
      {allowNewCellFilters && (
        <Button aria-describedby="simple-popover" onClick={() => setIsNewCellsModalOpen(true)}>
          New Cell Filters
        </Button>
      )}
      {allowNewCellFilters && isNewCellsModalOpen && (
        <NewCellsModal
          stains={stains}
          newCells={newCells}
          setNewCells={setNewCells}
          onClose={() => setIsNewCellsModalOpen(false)}
          getMappingFiltersMetadataForLogicalQuery={(stain: string) =>
            getMappingFiltersMetadataForLogicalQuery(stain, ActionInput.FEATURE_FAMILY, AttributesTable.CellTable)
          }
        />
      )}
    </>
  );
};
